<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  context: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const dialogRef = ref(null);
const copiedKey = ref('');

const previewImage = computed(
  () => props.context.imageUrl || props.context.thumbnailUrl || null
);

const channelLabel = computed(() => {
  if (props.context.channel === 'instagram') {
    return t('CONVERSATION.META_AD_CONTEXT.CHANNELS.INSTAGRAM');
  }
  if (props.context.channel === 'messenger') {
    return t('CONVERSATION.META_AD_CONTEXT.CHANNELS.MESSENGER');
  }
  if (props.context.channel === 'whatsapp') {
    return t('CONVERSATION.META_AD_CONTEXT.CHANNELS.WHATSAPP');
  }
  return t('CONVERSATION.META_AD_CONTEXT.CHANNELS.UNKNOWN');
});

const qualityLabel = computed(() =>
  props.context.attributionQuality === 'confirmed'
    ? t('CONVERSATION.META_AD_CONTEXT.QUALITY.CONFIRMED')
    : t('CONVERSATION.META_AD_CONTEXT.QUALITY.PARTIAL')
);
const detailRows = computed(() =>
  [
    [
      'adId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.AD_ID'),
      props.context.adId,
    ],
    [
      'adsetId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.ADSET_ID'),
      props.context.adsetId,
    ],
    [
      'campaignId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.CAMPAIGN_ID'),
      props.context.campaignId,
    ],
    [
      'creativeId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.CREATIVE_ID'),
      props.context.creativeId,
    ],
    [
      'ctwaClid',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.CTWA_CLID'),
      props.context.ctwaClid,
    ],
    [
      'objectStoryId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.OBJECT_STORY_ID'),
      props.context.objectStoryId,
    ],
    [
      'instagramMediaId',
      t('CONVERSATION.META_AD_CONTEXT.FIELDS.INSTAGRAM_MEDIA_ID'),
      props.context.instagramMediaId,
    ],
  ]
    .filter(([, , value]) => Boolean(value))
    .map(([key, label, value]) => ({ key, label, value }))
);

const openDetails = () => dialogRef.value?.open();

const copyValue = async (key, value) => {
  if (!navigator?.clipboard?.writeText) return;

  try {
    await navigator.clipboard.writeText(value);
    copiedKey.value = key;
    window.setTimeout(() => {
      if (copiedKey.value === key) copiedKey.value = '';
    }, 1500);
  } catch {
    copiedKey.value = '';
  }
};
</script>

<template>
  <li class="px-4 pt-3 pb-1 list-none" data-clarity-mask="True">
    <button
      type="button"
      class="flex items-center w-full gap-3 p-3 text-left border rounded-xl bg-n-alpha-1 border-n-blue-5 hover:border-n-blue-7 hover:bg-n-blue-2 transition-colors"
      @click="openDetails"
    >
      <div
        v-if="previewImage"
        class="overflow-hidden rounded-lg size-14 bg-n-slate-3 flex-shrink-0"
      >
        <img
          :src="previewImage"
          :alt="context.adName || context.title || ''"
          class="object-cover w-full h-full"
        />
      </div>
      <div
        v-else
        class="flex items-center justify-center rounded-lg size-14 bg-n-blue-3 text-n-blue-11 flex-shrink-0"
      >
        <span class="i-lucide-megaphone size-5" />
      </div>

      <div class="flex flex-col min-w-0 flex-1 gap-1">
        <div class="flex items-center gap-2">
          <span class="text-xs font-medium text-n-blue-11">
            {{ t('CONVERSATION.META_AD_CONTEXT.BANNER_TITLE') }}
          </span>
          <span
            class="px-1.5 py-0.5 text-[10px] font-medium rounded bg-n-slate-3 text-n-slate-11"
          >
            {{ channelLabel }}
          </span>
        </div>
        <span class="text-sm font-medium truncate text-n-slate-12">
          {{
            context.adName ||
            context.title ||
            t('CONVERSATION.META_AD_CONTEXT.AD_FALLBACK')
          }}
        </span>
        <div
          v-if="context.welcomeText"
          class="flex flex-col gap-0.5 p-2 mt-1 rounded-lg bg-n-slate-2"
        >
          <span class="text-[10px] font-medium text-n-slate-9">
            {{ t('CONVERSATION.META_AD_CONTEXT.WELCOME_MESSAGE') }}
          </span>
          <span
            class="text-xs whitespace-pre-line line-clamp-3 text-n-slate-11"
          >
            {{ context.welcomeText }}
          </span>
        </div>
        <span class="text-xs text-n-slate-10">
          {{ t('CONVERSATION.META_AD_CONTEXT.VIEW_DETAILS') }}
        </span>
      </div>

      <span
        class="i-lucide-chevron-right size-4 text-n-slate-9 flex-shrink-0"
      />
    </button>

    <Dialog
      ref="dialogRef"
      width="lg"
      :title="t('CONVERSATION.META_AD_CONTEXT.DIALOG_TITLE')"
      :description="t('CONVERSATION.META_AD_CONTEXT.DIALOG_DESCRIPTION')"
      :show-confirm-button="false"
      :cancel-button-label="t('CONVERSATION.META_AD_CONTEXT.CLOSE')"
      overflow-y-auto
    >
      <div class="flex flex-col gap-5" data-clarity-mask="True">
        <div
          v-if="previewImage"
          class="overflow-hidden rounded-xl bg-n-slate-3 max-h-80"
        >
          <img
            :src="previewImage"
            :alt="context.adName || context.title || ''"
            class="object-contain w-full max-h-80"
          />
        </div>
        <div class="flex flex-col gap-2">
          <div class="flex items-center gap-2">
            <span
              class="px-2 py-1 text-xs font-medium rounded-md bg-n-blue-3 text-n-blue-11"
            >
              {{ channelLabel }}
            </span>
            <span
              class="px-2 py-1 text-xs font-medium rounded-md bg-n-slate-3 text-n-slate-11"
            >
              {{ qualityLabel }}
            </span>
          </div>

          <h4
            v-if="context.adName || context.title"
            class="m-0 text-base font-semibold text-n-slate-12"
          >
            {{ context.adName || context.title }}
          </h4>
          <p
            v-if="context.body"
            class="m-0 text-sm whitespace-pre-line text-n-slate-11"
          >
            {{ context.body }}
          </p>
        </div>

        <div
          v-if="context.welcomeText"
          class="p-3 border rounded-xl border-n-blue-4 bg-n-blue-2"
        >
          <span class="block mb-1 text-xs font-medium text-n-blue-11">
            {{ t('CONVERSATION.META_AD_CONTEXT.WELCOME_MESSAGE') }}
          </span>
          <p class="m-0 text-sm whitespace-pre-line text-n-slate-12">
            {{ context.welcomeText }}
          </p>
        </div>

        <div
          v-if="
            context.campaignName || context.adsetName || context.creativeName
          "
          class="grid grid-cols-1 gap-3 p-3 rounded-xl bg-n-slate-2"
        >
          <div v-if="context.campaignName" class="flex flex-col gap-0.5">
            <span class="text-xs text-n-slate-9">
              {{ t('CONVERSATION.META_AD_CONTEXT.FIELDS.CAMPAIGN') }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ context.campaignName }}
            </span>
          </div>
          <div v-if="context.adsetName" class="flex flex-col gap-0.5">
            <span class="text-xs text-n-slate-9">
              {{ t('CONVERSATION.META_AD_CONTEXT.FIELDS.ADSET') }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ context.adsetName }}
            </span>
          </div>
          <div v-if="context.creativeName" class="flex flex-col gap-0.5">
            <span class="text-xs text-n-slate-9">
              {{ t('CONVERSATION.META_AD_CONTEXT.FIELDS.CREATIVE') }}
            </span>
            <span class="text-sm text-n-slate-12">
              {{ context.creativeName }}
            </span>
          </div>
        </div>

        <div
          v-if="detailRows.length"
          class="flex flex-col divide-y divide-n-weak"
        >
          <div
            v-for="row in detailRows"
            :key="row.key"
            class="flex items-center gap-3 py-2"
          >
            <div class="flex flex-col min-w-0 flex-1 gap-0.5">
              <span class="text-xs text-n-slate-9">{{ row.label }}</span>
              <code class="text-xs break-all text-n-slate-12">
                {{ row.value }}
              </code>
            </div>
            <Button
              variant="ghost"
              color="slate"
              size="sm"
              :icon="copiedKey === row.key ? 'i-lucide-check' : 'i-lucide-copy'"
              :label="
                copiedKey === row.key
                  ? t('CONVERSATION.META_AD_CONTEXT.COPIED')
                  : t('CONVERSATION.META_AD_CONTEXT.COPY')
              "
              @click="copyValue(row.key, row.value)"
            />
          </div>
        </div>

        <div
          v-if="
            context.firstTouchAdId && context.firstTouchAdId !== context.adId
          "
          class="p-3 border rounded-xl border-n-slate-4"
        >
          <span class="block mb-1 text-xs text-n-slate-9">
            {{ t('CONVERSATION.META_AD_CONTEXT.FIRST_TOUCH') }}
          </span>
          <code class="text-xs break-all text-n-slate-12">
            {{ context.firstTouchAdId }}
          </code>
        </div>

        <div class="flex flex-wrap gap-3">
          <a
            v-if="context.linkUrl"
            :href="context.linkUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm font-medium text-n-blue-11 hover:text-n-blue-12 hover:underline"
          >
            <span class="i-lucide-external-link size-4" />
            {{ t('CONVERSATION.META_AD_CONTEXT.OPEN_AD') }}
          </a>
          <a
            v-if="context.adsManagerUrl"
            :href="context.adsManagerUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-sm font-medium text-n-blue-11 hover:text-n-blue-12 hover:underline"
          >
            <span class="i-lucide-external-link size-4" />
            {{ t('CONVERSATION.META_AD_CONTEXT.OPEN_ADS_MANAGER') }}
          </a>
        </div>
      </div>
    </Dialog>
  </li>
</template>
